import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { Price } from '../components/Price';
import { useAuth } from '../lib/auth';
import { discountForBrand, useCustomer } from '../lib/customer';
import { useProjectList, type ProjectItem } from '../lib/projectList';
import { Seo } from '../lib/seo';
import { supabase, type ProductPublic } from '../lib/supabase';
import { landingPaths } from '../components/landingData';
import './RailBuilder.css';

type RailProduct = ProductPublic & {
  product_type?: string | null;
  category_paths?: string[] | null;
};

type RailVariant = {
  id: string;
  product_id: string;
  full_sku: string;
  attributes: Record<string, string>;
  msrp_usd: number | null;
  color_temperature_k: number | null;
  beam_angle_deg: number | null;
  finish: string | null;
  wattage_w: number | null;
  dim_method: string | null;
  primary_image_url: string | null;
};

type RailInventory = {
  product_id: string;
  variant_id: string | null;
  on_hand: number;
  reserved: number;
};

type SystemId = 'micro5' | 'micro10' | 'architectural20';
type Mounting = 'sobrepuesto' | 'empotrado';
type DimMethod = 'TRIAC' | 'DALI';

const DIM_METHODS: DimMethod[] = ['TRIAC', 'DALI'];

type SystemDefinition = {
  id: SystemId;
  label: string;
  image: string;
  width: number;
  voltage: string;
  path: string;
  eyebrow: string;
  description: string;
  advantage: string;
  tradeoff: string;
  trackSku: Record<Mounting, string>;
  initialSku: string;
  capSku: Partial<Record<Mounting, string>>;
  unionSku: Record<Mounting, string>;
  electricalUnionSku?: string;
  cornerElectricalSku?: string;
  cornerSku: Record<Mounting, string>;
  driverSkus: string[];
};

const SYSTEMS: SystemDefinition[] = [
  {
    id: 'micro5', label: 'Micro 5', image: '/product-images/rail-system-micro5-transparent.png', width: 5, voltage: '24V', path: 'Riel Magnético/Micro Riel Magnético 24V',
    eyebrow: 'El más discreto',
    description: 'Una línea mínima que casi desaparece en la arquitectura.',
    advantage: 'La integración más limpia y elegante.', tradeoff: 'Familia de luminarias de escala compacta.',
    trackSku: { sobrepuesto: 'RMS5MM24_XM_DIM_N', empotrado: 'RME5MM24_XM_DIM_N' },
    initialSku: 'RMS5MM24_CON_DIM_N',
    capSku: { sobrepuesto: 'RMS5MM_ACCT_N', empotrado: 'RMS5MM_ACCT_N' },
    unionSku: { sobrepuesto: 'ACCRM5MMCON_DIM_N', empotrado: 'ACCRM5MMCON_DIM_N' },
    electricalUnionSku: 'RMS5MM24_INT_DIM_N',
    cornerSku: { sobrepuesto: 'RMS5MM_ESQ_N', empotrado: 'RME5MM_ESQ_DIM_N' },
    driverSkus: ['DRM5MM127/2475_SW_N', 'DRM5MM127/24150_SW_N', 'DRM5MM127/24350_SW_N'],
  },
  {
    id: 'micro10', label: 'Micro 10', image: '/product-images/rail-system-micro10-transparent.png', width: 10, voltage: '48V', path: 'Riel Magnético/Riel Magnético 10 mm 48V',
    eyebrow: 'El punto medio',
    description: 'Perfil delgado con mayor versatilidad en acabados de luminarias.',
    advantage: 'Más opciones de acabado y combinación visual.', tradeoff: 'Algunos acabados se cotizan bajo pedido.',
    trackSku: { sobrepuesto: 'RMS10MM48_XM', empotrado: 'RME10MM48_XM' },
    initialSku: 'CON10MM48V',
    capSku: { sobrepuesto: 'TP10MM_SOB' },
    unionSku: { sobrepuesto: 'ACC10MM_SOB', empotrado: 'ACC10MM_SOB' },
    cornerElectricalSku: 'INT10MM48V_L',
    cornerSku: { sobrepuesto: 'ESQ10MM_SOB', empotrado: 'ESQ10MM_EMP' },
    driverSkus: ['FP127_48V200W_DIM_IP20'],
  },
  {
    id: 'architectural20', label: 'Arquitectónico 20', image: '/product-images/rail-system-architectural20-transparent.png', width: 20, voltage: '48V', path: 'Riel Magnético/Riel Magnético 48V',
    eyebrow: 'La opción versátil',
    description: 'El sistema de mejor precio y con la familia más amplia.',
    advantage: 'Más potencia, accesorios y libertad de composición.', tradeoff: 'El perfil tiene mayor presencia visual.',
    trackSku: { sobrepuesto: 'RMS48_XM_DIM_N', empotrado: 'RME48_XM_DIM_N' },
    initialSku: 'RMCON48_DIM_N',
    capSku: { sobrepuesto: 'TRMS48_DIM_N', empotrado: 'TRME48_DIM_N' },
    unionSku: { sobrepuesto: 'ACRMSO20MM_DIM_N', empotrado: 'ACRMEM20MM_DIM_N' },
    electricalUnionSku: 'RMINT48_IDIM_N',
    cornerElectricalSku: 'RMINT48_LDIM_N',
    cornerSku: { sobrepuesto: 'RMS48_ESQ_DIM_N', empotrado: 'RME48_ESQ_DIM_N' },
    driverSkus: ['DRM127/48_DIM_N'],
  },
];

const RAIL_PATHS = landingPaths.filter(path => path.startsWith('Riel Magnético'));

const imageOf = (product?: RailProduct | null) => product?.images?.find(image => image.is_thumbnail)?.url ?? product?.images?.[0]?.url ?? '';
const numberIn = (value: unknown) => Number(String(value ?? '').replace(',', '.').match(/\d+(?:\.\d+)?/)?.[0] ?? 0);
const wattsOf = (product: RailProduct, variant?: RailVariant | null) =>
  Number(variant?.wattage_w ?? 0) || numberIn(product.name.match(/\d+(?:\.\d+)?\s*W/i)?.[0]) || numberIn(product.specs?.potencia);

function modularizeRun(length: number) {
  const usefulMeters = Math.max(0, Number(length) || 0);
  const purchaseMeters = Math.ceil(usefulMeters);
  const twoMeterProfiles = Math.floor(purchaseMeters / 2);
  const oneMeterProfiles = purchaseMeters % 2;
  const pieces = twoMeterProfiles + oneMeterProfiles;
  return { usefulMeters, purchaseMeters, twoMeterProfiles, oneMeterProfiles, pieces, unions: Math.max(0, pieces - 1) };
}

function isLuminaire(product: RailProduct) {
  const path = product.category_paths?.join(' ') ?? '';
  return ['proyector', 'lineal', 'downlight', 'raster'].includes(product.product_type ?? '')
    || /\/(Proyectores|Lineal|Lineales|Lineal flexible|Downlights|Raster)$/.test(path);
}

function productHref(product: RailProduct, variant?: RailVariant | null) {
  const query = variant?.full_sku ? `?v=${encodeURIComponent(variant.full_sku)}` : '';
  return `/producto/${encodeURIComponent(product.sku)}${query}`;
}

function variantDimMethod(product: RailProduct, variant: RailVariant): DimMethod | null {
  const declared = String(variant.dim_method ?? '').trim().toUpperCase();
  if (declared.includes('DALI')) return 'DALI';
  if (declared.includes('TRIAC')) return 'TRIAC';
  const sku = `${variant.full_sku} ${product.sku}`.toUpperCase();
  if (sku.includes('DALI')) return 'DALI';
  if (sku.includes('TRIAC') || sku.includes('_DIM_')) return 'TRIAC';
  return null;
}

export function RailBuilder() {
  const [products, setProducts] = useState<RailProduct[]>([]);
  const [variants, setVariants] = useState<RailVariant[]>([]);
  const [inventory, setInventory] = useState<RailInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [systemId, setSystemId] = useState<SystemId>('micro10');
  const [mounting, setMounting] = useState<Mounting>('sobrepuesto');
  const [runs, setRuns] = useState<number[]>([3]);
  const [corners, setCorners] = useState(0);
  const [circuits, setCircuits] = useState(1);
  const [cct, setCct] = useState(3000);
  const [finish, setFinish] = useState('Negro');
  const [dimMethod, setDimMethod] = useState<DimMethod>('TRIAC');
  const [lightQty, setLightQty] = useState<Record<string, number>>({});
  const [added, setAdded] = useState(false);
  const { add } = useProjectList();
  const { session } = useAuth();
  const { customer } = useCustomer();
  const discount = discountForBrand(customer, 'nuled');
  const canViewPrices = !!session;
  const canViewDealerPrice = !!session && !!customer && discount > 0;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('products_public')
        .select('*')
        .overlaps('category_paths', RAIL_PATHS)
        .eq('status', 'active')
        .limit(1000);
      if (!alive) return;
      if (error) {
        setLoadError(error.message);
        setLoading(false);
        return;
      }
      const list = (data as RailProduct[]) ?? [];
      const ids = list.map(product => product.id);
      const { data: variantRows, error: variantError } = ids.length
        ? await supabase
          .from('product_variants')
          .select('id, product_id, full_sku, attributes, msrp_usd, color_temperature_k, beam_angle_deg, finish, wattage_w, dim_method, primary_image_url')
          .in('product_id', ids)
          .eq('active', true)
          .limit(3000)
        : { data: [], error: null };
      const { data: inventoryRows, error: inventoryError } = ids.length
        ? await supabase
          .from('inventory')
          .select('product_id, variant_id, on_hand, reserved')
          .in('product_id', ids)
          .limit(5000)
        : { data: [], error: null };
      if (!alive) return;
      if (variantError) setLoadError(variantError.message);
      if (inventoryError) setLoadError(inventoryError.message);
      setProducts(list);
      setVariants((variantRows as RailVariant[]) ?? []);
      setInventory((inventoryRows as RailInventory[]) ?? []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const system = SYSTEMS.find(item => item.id === systemId)!;
  const bySku = (sku?: string) => products.find(product => product.sku === sku) ?? null;
  const systemProducts = useMemo(() => products.filter(product =>
    product.category_paths?.some(path => path === system.path || path.startsWith(`${system.path}/`)),
  ), [products, system.path]);
  const luminaireProducts = useMemo(() => systemProducts.filter(isLuminaire), [systemProducts]);
  const dimmingOptions = useMemo(() => DIM_METHODS.filter(method =>
    luminaireProducts.some(product => variants.some(variant =>
      variant.product_id === product.id && variantDimMethod(product, variant) === method,
    )),
  ), [luminaireProducts, variants]);

  useEffect(() => {
    if (dimmingOptions.length && !dimmingOptions.includes(dimMethod)) setDimMethod(dimmingOptions[0]);
  }, [dimmingOptions, dimMethod]);

  const variantsFor = (productId: string) => variants.filter(variant => variant.product_id === productId);
  const inventoryFor = (productId: string, variantId?: string | null) => {
    const variantRows = variantId ? inventory.filter(row => row.product_id === productId && row.variant_id === variantId) : [];
    const rows = variantRows.length ? variantRows : inventory.filter(row => row.product_id === productId && row.variant_id == null);
    return rows.reduce((sum, row) => sum + Math.max(0, Number(row.on_hand || 0) - Number(row.reserved || 0)), 0);
  };
  const pickVariant = (product: RailProduct) => {
    const available = variantsFor(product.id);
    if (!available.length) return null;
    const withControl = available.filter(variant => variantDimMethod(product, variant) === dimMethod);
    if (!withControl.length) return null;
    const withCct = withControl.filter(variant => variant.color_temperature_k == null || variant.color_temperature_k === cct);
    const withFinish = withCct.filter(variant => variant.finish == null || variant.finish === finish);
    return withFinish.find(variant => variant.beam_angle_deg === 24)
      ?? withFinish.find(variant => variant.beam_angle_deg === 36)
      ?? withFinish[0]
      ?? withCct[0]
      ?? withControl[0];
  };

  const compatibleLights = useMemo(() => luminaireProducts.filter(product => {
    const available = variants.filter(variant => variant.product_id === product.id);
    if (!available.length) return true;
    const withControl = available.filter(variant => variantDimMethod(product, variant) === dimMethod);
    if (!withControl.length) return false;
    const hasCct = withControl.every(variant => variant.color_temperature_k != null);
    const hasFinish = withControl.some(variant => variant.finish != null);
    return (!hasCct || withControl.some(variant => variant.color_temperature_k === cct))
      && (!hasFinish || withControl.some(variant => variant.finish === finish && (variant.color_temperature_k == null || variant.color_temperature_k === cct)));
  }), [luminaireProducts, variants, cct, finish, dimMethod]);

  useEffect(() => {
    setLightQty(current => Object.fromEntries(Object.entries(current).filter(([id]) => compatibleLights.some(product => product.id === id))));
  }, [systemId, cct, finish, dimMethod, compatibleLights]);

  const trackProduct = bySku(system.trackSku[mounting]);
  const trackImage = systemId === 'micro5'
    ? `/product-images/micro5-${mounting}-transparent.png`
    : imageOf(trackProduct);
  const mountingLabel = mounting === 'sobrepuesto' ? 'sobrepuesto / suspendido' : 'empotrado';
  const modulePlan = useMemo(() => runs.map(modularizeRun), [runs]);
  const totalMeters = runs.reduce((sum, length) => sum + Math.max(0, Number(length) || 0), 0);
  const purchaseMeters = modulePlan.reduce((sum, run) => sum + run.purchaseMeters, 0);
  const twoMeterProfiles = modulePlan.reduce((sum, run) => sum + run.twoMeterProfiles, 0);
  const oneMeterProfiles = modulePlan.reduce((sum, run) => sum + run.oneMeterProfiles, 0);
  const straightUnions = modulePlan.reduce((sum, run) => sum + run.unions, 0);
  const cornerMechanicalUnions = corners * 2;
  const mechanicalUnions = straightUnions + cornerMechanicalUnions;
  const profileBreakdown = [twoMeterProfiles && `${twoMeterProfiles} × 2 m`, oneMeterProfiles && `${oneMeterProfiles} × 1 m`].filter(Boolean).join(' + ');
  const moduleDetail = modulePlan.map((run, index) => {
    const pieces = [run.twoMeterProfiles && `${run.twoMeterProfiles}×2 m`, run.oneMeterProfiles && `${run.oneMeterProfiles}×1 m`].filter(Boolean).join(' + ');
    return `Tramo ${index + 1}: ${pieces || '0 m'}`;
  }).join(' · ');
  const trackVariants = trackProduct ? variantsFor(trackProduct.id) : [];
  const trackVariantForLength = (meters: 1 | 2) => trackVariants.find(variant =>
    new RegExp(`_${meters}M(?:_|$)`, 'i').test(variant.full_sku)
    || String(variant.attributes?.dimensiones ?? '').trim().toLowerCase() === `${meters}m`,
  ) ?? null;
  const oneMeterTrackVariant = trackVariantForLength(1);
  const twoMeterTrackVariant = trackVariantForLength(2);
  const cuts = runs.filter(length => Math.abs(length - Math.round(length)) > .001).length;
  const selectedLights = compatibleLights
    .map(product => ({ product, qty: lightQty[product.id] ?? 0, variant: pickVariant(product) }))
    .filter(line => line.qty > 0);
  const totalWatts = selectedLights.reduce((sum, line) => sum + wattsOf(line.product, line.variant) * line.qty, 0);
  const requiredWatts = Math.ceil(totalWatts * 1.2);

  const driverChoices = system.driverSkus.map(sku => bySku(sku)).filter((product): product is RailProduct => !!product);
  const selectedDriver = driverChoices.find(product => numberIn(product.specs?.potencia) >= requiredWatts) ?? driverChoices.at(-1) ?? null;
  const driverCapacity = selectedDriver ? numberIn(selectedDriver.specs?.potencia) || (systemId === 'micro10' ? 200 : 150) : 0;
  const driverQty = totalWatts > 0 && driverCapacity > 0 ? Math.max(circuits, Math.ceil(requiredWatts / driverCapacity)) : 0;
  const reference = `RIEL-${system.width}-${mounting === 'sobrepuesto' ? 'SOB' : 'EMP'}-${totalMeters.toFixed(1)}M-${dimMethod}`;

  const parts = useMemo(() => {
    const rows: { product: RailProduct; variant?: RailVariant | null; qty: number; label: string; notes: string }[] = [];
    const push = (product: RailProduct | null, qty: number, label: string, notes: string, variant?: RailVariant | null) => {
      if (product && qty > 0) rows.push({ product, variant, qty, label, notes });
    };
    if (twoMeterTrackVariant || oneMeterTrackVariant) {
      push(trackProduct, twoMeterProfiles, 'Perfil magnético · 2 m', `${twoMeterProfiles} módulos de 2 m · ${mountingLabel}`, twoMeterTrackVariant);
      push(trackProduct, oneMeterProfiles, 'Perfil magnético · 1 m', `${oneMeterProfiles} módulos de 1 m · ${mountingLabel}`, oneMeterTrackVariant);
    } else {
      push(trackProduct, purchaseMeters, 'Perfil magnético', `${totalMeters.toFixed(1)} m útiles · ${profileBreakdown} · ${mountingLabel}`);
    }
    push(bySku(system.initialSku), circuits, 'Alimentación', `${circuits} ${circuits === 1 ? 'circuito' : 'circuitos'}`);
    push(bySku(system.capSku[mounting]), circuits, 'Terminación', 'Tapas inicial y final');
    push(bySku(system.unionSku[mounting]), mechanicalUnions, 'Unión mecánica', `${straightUnions} entre módulos rectos · ${cornerMechanicalUnions} para ${corners} ${corners === 1 ? 'esquina' : 'esquinas'}`);
    push(bySku(system.electricalUnionSku), straightUnions, 'Interconexión eléctrica', `${straightUnions} interconectores entre módulos`);
    push(bySku(system.cornerElectricalSku), corners, 'Interconexión eléctrica en L', `${corners} ${corners === 1 ? 'interconector angular' : 'interconectores angulares'}`);
    push(bySku(system.cornerSku[mounting]), corners, 'Cambio de dirección', `${corners} esquinas a 90°`);
    push(selectedDriver, driverQty, 'Fuente de poder', `${totalWatts}W instalados · ${requiredWatts}W mínimos con reserva · control ${dimMethod}`);
    return rows;
  }, [products, trackProduct, purchaseMeters, totalMeters, mounting, circuits, straightUnions, cornerMechanicalUnions, mechanicalUnions, corners, selectedDriver, driverQty, totalWatts, requiredWatts, dimMethod, system, twoMeterProfiles, oneMeterProfiles, twoMeterTrackVariant, oneMeterTrackVariant, profileBreakdown]);

  const publicTotal = parts.reduce((sum, row) => sum + Number(row.variant?.msrp_usd || row.product.msrp_usd || 0) * row.qty, 0)
    + selectedLights.reduce((sum, line) => sum + Number(line.variant?.msrp_usd || line.product.msrp_usd || 0) * line.qty, 0);
  const complete = !!trackProduct && totalMeters > 0 && selectedLights.length > 0 && !!selectedDriver;

  const setRun = (index: number, value: number) => setRuns(current => current.map((length, i) => i === index ? Math.max(.1, value || .1) : length));
  const changeQty = (productId: string, delta: number) => setLightQty(current => ({ ...current, [productId]: Math.max(0, (current[productId] ?? 0) + delta) }));

  const toProjectItem = (product: RailProduct, qty: number, notes: string, variant?: RailVariant | null): Omit<ProjectItem, 'qty'> & { qty: number } => {
    const msrp = Number(variant?.msrp_usd || product.msrp_usd || 0);
    return {
      product_id: product.id,
      sku: variant?.full_sku ?? product.sku,
      name: product.name,
      brand_code: product.brand_code,
      msrp_usd: msrp || null,
      cost_usd: canViewDealerPrice && msrp ? msrp * (1 - discount / 100) : undefined,
      image_url: variant?.primary_image_url || imageOf(product),
      qty,
      unit: product.unit,
      notes: `${reference} · ${notes}`,
    };
  };

  const addSystem = () => {
    if (!session || !complete) return;
    parts.forEach(row => add(toProjectItem(row.product, row.qty, row.notes, row.variant)));
    selectedLights.forEach(line => add(toProjectItem(line.product, line.qty, `${cct}K · ${finish} · ${dimMethod}`, line.variant)));
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };

  if (loading) return <RailShell><div className="rail-loading">Preparando familias de riel…</div></RailShell>;
  if (loadError) return <RailShell><div className="rail-loading">No pudimos cargar los rieles: {loadError}</div></RailShell>;

  return (
    <RailShell>
      <section className="rail-hero">
        <div className="rail-hero-copy">
          <div className="rail-kicker">NULED Track Studio</div>
          <h1>Arma tu riel.</h1>
          <p>Elige la escala, dibuja el recorrido y combina la luz. Nosotros completamos la parte técnica.</p>
          <a href="#configurador" className="rail-primary-link">Empezar configuración</a>
        </div>
        <div className="rail-hero-products" aria-label="Las tres escalas de riel magnético NULED">
          {SYSTEMS.map(item => <figure key={item.id}><img src={item.image} alt={`${item.label}, riel de ${item.width} milímetros`} /><figcaption><span>{item.label}</span><b>{item.width} mm</b></figcaption></figure>)}
        </div>
      </section>

      <section className="rail-intro">
        <div><div className="rail-kicker">Una decisión sencilla</div><h2>Más delgado, más discreto.<br />Más amplio, más flexible.</h2></div>
        <p>Los tres sistemas resuelven iluminación de acento y lineal. La diferencia está en cuánto quieres ver el riel, cuánta variedad necesitas y cuánto quieres invertir.</p>
      </section>

      <section className="rail-builder" id="configurador">
        <div className="rail-builder-heading"><div><div className="rail-kicker">Configurador</div><h2>Diseña el recorrido.</h2></div><span>El listado técnico se actualiza al instante</span></div>

        <RailStep number="01" title="Elige la escala" description="Empieza por la presencia visual que buscas.">
          <div className="rail-system-grid">
            {SYSTEMS.map(item => (
              <button key={item.id} className={systemId === item.id ? 'selected' : ''} onClick={() => setSystemId(item.id)}>
                <span className="system-photo"><img src={item.image} alt="" /></span>
                <span className="system-top"><small>{item.eyebrow}</small></span>
                <strong>{item.label}</strong><em>{item.width} mm · {item.voltage}</em><i style={{ height: `${Math.max(4, item.width / 2)}px` }} />
                <p>{item.description}</p><dl><div><dt>+</dt><dd>{item.advantage}</dd></div><div><dt>–</dt><dd>{item.tradeoff}</dd></div></dl>
              </button>
            ))}
          </div>
        </RailStep>

        <RailStep number="02" title="Tipo de instalación" description="Define cómo se integra el perfil a la arquitectura.">
          <div className="mounting-grid">
            <button className={mounting === 'sobrepuesto' ? 'selected' : ''} onClick={() => setMounting('sobrepuesto')}><strong>Sobrepuesto / suspendido</strong><small>El mismo perfil se instala sobre plafón, muro o con suspensión.</small></button>
            <button className={mounting === 'empotrado' ? 'selected' : ''} onClick={() => setMounting('empotrado')}><strong>Empotrado</strong><small>Integración limpia dentro del plafón.</small></button>
          </div>
          {trackProduct && <div className="selected-track"><img src={trackImage} alt="" /><div><small>RIEL SELECCIONADO</small><Link to={productHref(trackProduct)}>{trackProduct.name}</Link><span>{system.voltage} · Negro · {mountingLabel}</span></div></div>}
        </RailStep>

        <RailStep number="03" title="Traza el recorrido" description="Agrega cada tramo recto. Nosotros calculamos perfil, cortes y uniones.">
          <div className="run-editor">
            {runs.map((length, index) => (
              <div className="run-row" key={index}><span>Tramo {index + 1}</span><label><input type="number" min="0.1" step="0.1" value={length} onChange={event => setRun(index, Number(event.target.value))} /> m</label>{runs.length > 1 && <button aria-label={`Eliminar tramo ${index + 1}`} onClick={() => setRuns(current => current.filter((_, i) => i !== index))}>×</button>}</div>
            ))}
            <button className="add-run" onClick={() => setRuns(current => [...current, 1])}>+ Agregar tramo</button>
          </div>
          <div className="layout-options">
            <label>Esquinas de 90° <span><button onClick={() => setCorners(value => Math.max(0, value - 1))}>−</button><b>{corners}</b><button onClick={() => setCorners(value => value + 1)}>+</button></span></label>
            <label>Circuitos independientes <span><button onClick={() => setCircuits(value => Math.max(1, value - 1))}>−</button><b>{circuits}</b><button onClick={() => setCircuits(value => value + 1)}>+</button></span></label>
          </div>
          <div className="cut-plan"><div><b>{totalMeters.toFixed(1)} m</b><span>recorrido útil</span></div><div><b>{purchaseMeters} m</b><span>perfil a comprar</span></div><div><b>{mechanicalUnions}</b><span>uniones mecánicas</span></div><div><b>{cuts}</b><span>{cuts === 1 ? 'corte final' : 'cortes finales'}</span></div></div>
          <div className="module-plan"><strong>Despiece modular</strong><span>{moduleDetail}</span><small>{profileBreakdown || 'Agrega una longitud'} · módulos disponibles de 2 m y 1 m</small></div>
        </RailStep>

        <RailStep number="04" title="Añade la luz" description="Combina funciones y cantidades dentro de la misma familia de riel.">
          <div className="light-settings">
            <div><span>Temperatura</span><button className={cct === 2700 ? 'selected' : ''} onClick={() => setCct(2700)}>2700K</button><button className={cct === 3000 ? 'selected' : ''} onClick={() => setCct(3000)}>3000K</button></div>
            <div><span>Acabado</span><button className={finish === 'Negro' ? 'selected' : ''} onClick={() => setFinish('Negro')}>Negro</button><button className={finish === 'Blanco' ? 'selected' : ''} onClick={() => setFinish('Blanco')}>Blanco</button></div>
            <div><span>Atenuación</span>{dimmingOptions.map(method => <button key={method} className={dimMethod === method ? 'selected' : ''} onClick={() => setDimMethod(method)}>{method}</button>)}</div>
          </div>
          {systemId === 'micro10' && <p className="rail-catalog-note"><strong>Más acabados disponibles.</strong> La plataforma muestra una selección de la familia Micro 10; podemos cotizar otros acabados bajo pedido.</p>}
          <div className="rail-light-grid">
            {compatibleLights.map(product => {
              const variant = pickVariant(product);
              const qty = lightQty[product.id] ?? 0;
              const available = inventoryFor(product.id, variant?.id);
              return <article className={qty > 0 ? 'selected' : ''} key={product.id}><Link to={productHref(product, variant)} className="light-image"><img src={variant?.primary_image_url || imageOf(product)} alt={product.name} /></Link><div><small>{wattsOf(product, variant) || '—'}W · {product.product_type || 'Luminaria'}</small><Link to={productHref(product, variant)}>{product.name}</Link><code>{variant?.full_sku ?? product.sku}</code><span className={`stock-badge ${available > 0 ? 'in-stock' : 'made-to-order'}`}>{available > 0 ? `Disponible · ${available} pzas.` : 'Bajo pedido · 8–9 semanas'}</span><div className="qty-control"><button aria-label={`Quitar ${product.name}`} onClick={() => changeQty(product.id, -1)}>−</button><b>{qty}</b><button aria-label={`Agregar ${product.name}`} onClick={() => changeQty(product.id, 1)}>+</button></div></div></article>;
            })}
          </div>
          {!compatibleLights.length && <div className="rail-warning">No hay luminarias con esta combinación de temperatura, acabado y atenuación.</div>}
        </RailStep>

        <section className="rail-summary">
          <div className="rail-summary-title"><div><div className="rail-kicker">Tu sistema</div><h2>{reference}</h2><p>{system.label} · {mountingLabel} · atenuación {dimMethod} · {runs.length} {runs.length === 1 ? 'tramo' : 'tramos'}</p></div><span className={complete ? 'complete' : ''}>{complete ? 'Listo para cotizar' : 'Agrega luminarias'}</span></div>
          <div className="rail-summary-grid">
            <div className="bom-list">
              <h3>Lista automática de materiales</h3>
              {parts.map(row => <BomLine key={`${row.product.sku}-${row.label}`} product={row.product} variant={row.variant} qty={row.qty} label={row.label} price={canViewPrices ? Number(row.variant?.msrp_usd || row.product.msrp_usd || 0) : null} />)}
              {selectedLights.map(line => <BomLine key={line.product.id} product={line.product} variant={line.variant} qty={line.qty} label="Luminaria" price={canViewPrices ? Number(line.variant?.msrp_usd || line.product.msrp_usd || 0) : null} />)}
            </div>
            <aside className="rail-total">
              <dl><div><dt>Longitud útil</dt><dd>{totalMeters.toFixed(1)} m</dd></div><div><dt>Atenuación</dt><dd>{dimMethod}</dd></div><div><dt>Luminarias</dt><dd>{selectedLights.reduce((sum, line) => sum + line.qty, 0)}</dd></div><div><dt>Potencia instalada</dt><dd>{totalWatts} W</dd></div><div><dt>Fuente calculada</dt><dd>{driverQty ? `${driverQty} × ${driverCapacity}W` : 'Pendiente'}</dd></div><div><dt>Reserva eléctrica</dt><dd>20%</dd></div></dl>
              {canViewPrices ? <div className="rail-price"><small>Precio público estimado</small><Price usd={publicTotal} size="lg" /></div> : <div className="rail-price-locked"><span>Precios disponibles con sesión</span><Link to="/login">Inicia sesión para consultarlos</Link></div>}
              {canViewDealerPrice && <div className="rail-dealer-price">Tu precio distribuidor: ${(publicTotal * (1 - discount / 100)).toFixed(2)} USD</div>}
              {session ? <button className="rail-add" disabled={!complete} onClick={addSystem}>{added ? '✓ Sistema agregado' : 'Agregar todo a cotización'}</button> : <Link className="rail-add" to="/login">Inicia sesión para cotizar</Link>}
              <p>Se agregan las luminarias y todos los accesorios como partidas separadas, con cantidades y SKUs para compra.</p>
            </aside>
          </div>
        </section>
      </section>
    </RailShell>
  );
}

function RailShell({ children }: { children: React.ReactNode }) {
  return <div className="rail-experience rail-dark"><Seo title="Arma tu riel · Configurador de riel magnético NULED" description="Configura riel magnético de 5, 10 o 20 mm, calcula tramos, luminarias, conexiones y fuente de poder." canonical="https://nuled.mx/arma-tu-riel" /><Header visualNav variant="compose" /><main className="rail-page">{children}</main><Footer /></div>;
}

function RailStep({ number, title, description, children }: { number: string; title: string; description: string; children: React.ReactNode }) {
  return <section className="rail-step"><header><span>{number}</span><div><h3>{title}</h3><p>{description}</p></div></header><div className="rail-step-content">{children}</div></section>;
}

function BomLine({ product, variant, qty, label, price }: { product: RailProduct; variant?: RailVariant | null; qty: number; label: string; price: number | null }) {
  return <div className="bom-line"><Link to={productHref(product, variant)}><img src={variant?.primary_image_url || imageOf(product)} alt="" /></Link><div><small>{label}</small><Link to={productHref(product, variant)}>{product.name}</Link><code>{variant?.full_sku ?? product.sku}</code></div><b>× {qty}</b>{price != null && price > 0 && <Price usd={price * qty} size="sm" showSecondary={false} />}</div>;
}
